import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Clock, ListChecks, ArrowRight, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ExamDetail() {
  const { eid } = useParams();
  const [exam, setExam] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const loadAttempts = () =>
    api.get("/attempts").then((r) => setAttempts((r.data || []).filter((a) => a.exam_id === eid)));

  useEffect(() => {
    api.get(`/exams/${eid}`).then((r) => setExam(r.data));
    loadAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  const inProgress = attempts.find((a) => a.status === "in_progress");
  const submittedAttempts = attempts.filter((a) => a.status === "submitted");
  const bestScore = submittedAttempts.length
    ? Math.max(...submittedAttempts.map((a) => a.score || 0))
    : null;
  const hasPrior = submittedAttempts.length > 0;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/exams/${eid}/start`);
      navigate(`/app/exam/${eid}/take?attempt=${r.data.attempt_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start exam");
    } finally {
      setBusy(false);
    }
  };

  if (!exam) return <div className="text-sm text-zinc-500">Loading…</div>;

  const primaryLabel = inProgress ? "Resume" : hasPrior ? "Retake exam" : "Start exam";

  return (
    <div>
      <PageHeader overline="Exam Details" title={exam.title} subtitle={exam.description} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-0 border border-zinc-200 mb-6">
        {[
          { label: "Duration", value: `${exam.duration_minutes} min`, icon: Clock },
          { label: "Questions", value: exam.question_ids?.length || 0, icon: ListChecks },
          { label: "Total Marks", value: exam.total_marks },
          { label: "Passing", value: exam.passing_marks },
          { label: "Price", value: exam.price_credits > 0 ? `${exam.price_credits} cr` : "Free" },
          { label: "Negative", value: exam.negative_marking ? "Yes" : "No" },
        ].map((s) => (
          <div key={s.label} className="p-4 sm:p-6 border-r border-b border-zinc-200 last:border-r-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{s.label}</div>
            <div className="font-display text-xl sm:text-2xl font-bold tabular">{s.value}</div>
          </div>
        ))}
        <div className="col-span-2 sm:col-span-3 lg:col-span-1 p-4 sm:p-6 flex items-center">
          <button
            onClick={inProgress
              ? () => navigate(`/app/exam/${eid}/take?attempt=${inProgress.attempt_id}`)
              : start}
            disabled={busy}
            className="w-full bg-zinc-900 text-white px-4 py-3 text-sm font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid={hasPrior && !inProgress ? "retake-exam-btn" : "start-exam-btn"}
          >
            {busy ? "Starting…" : primaryLabel}
            {hasPrior && !inProgress ? <ArrowsClockwise size={14} weight="bold" /> : <ArrowRight size={14} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Prior attempts / retake summary */}
      {hasPrior && (
        <div className="border border-zinc-200 mb-6" data-testid="prior-attempts">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Your history</div>
              <div className="text-sm text-zinc-700 mt-1">
                {submittedAttempts.length} completed attempt{submittedAttempts.length > 1 ? "s" : ""} · Best score:
                <span className="font-mono-tab font-bold ml-1">{bestScore}/{exam.total_marks}</span>
              </div>
            </div>
            {!inProgress && (
              <button
                onClick={start}
                disabled={busy}
                className="hidden md:inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 text-sm font-medium hover:bg-zinc-900 hover:text-white disabled:opacity-50"
                data-testid="retake-exam-btn-inline"
              >
                <ArrowsClockwise size={14} /> Retake
              </button>
            )}
          </div>
          <div className="divide-y divide-zinc-200">
            {submittedAttempts.slice(0, 5).map((a) => (
              <Link
                to={`/app/results/${a.attempt_id}`}
                key={a.attempt_id}
                className="flex items-center justify-between px-6 py-3 hover:bg-zinc-50"
                data-testid={`prior-attempt-${a.attempt_id}`}
              >
                <div>
                  <div className="text-sm font-medium">
                    Attempt on {new Date(a.submitted_at || a.started_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">Attempt ID: {a.attempt_id}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score</div>
                    <div className="font-mono-tab text-sm font-bold">{a.score} / {a.total_marks}</div>
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold border px-2 py-1 ${a.result?.passed ? "border-green-600 text-green-700 bg-green-50" : "border-red-600 text-red-700 bg-red-50"}`}>
                    {a.result?.passed ? <><CheckCircle size={10} className="inline mr-1" />Pass</> : "Fail"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="border border-zinc-200 p-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Important instructions</div>
        <ul className="text-sm text-zinc-700 space-y-2 list-disc pl-5">
          <li>Once started, the timer will run continuously. The exam auto-submits when time expires.</li>
          <li>Your answers are saved automatically as you navigate.</li>
          <li>Use the question navigator to jump between questions and mark for review.</li>
          {exam.negative_marking && <li><strong>Negative marking is enabled</strong>. Incorrect answers deduct marks.</li>}
          {hasPrior && <li><strong>Retake:</strong> Starting again creates a new attempt. Your best score is retained on the leaderboard.</li>}
          <li>Do not refresh during the exam. Your session will resume from where you left off.</li>
        </ul>
      </div>
    </div>
  );
}
